import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, signal } from '@angular/core';
import { IconComponent } from '../icon/icon.component';
import { DropdownComponent, DropdownOption } from '../widgets/dropdown/dropdown.component';

interface FxRate { code: string; name: string; symbol: string; rate: number; eff: string; }

// rate = value of 1 USD in that currency (1 USD = rate × currency).
// Mirrors Reference Data → Currencies & FX (design snapshot).
const FX_RATES: FxRate[] = [
  { code: 'USD', name: 'US Dollar', symbol: '$', rate: 1.0, eff: '01 Jan 2026' },
  { code: 'EUR', name: 'Euro', symbol: '€', rate: 0.918, eff: '07 Jun 2026' },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', rate: 3.6725, eff: '01 Jan 2026' },
  { code: 'GBP', name: 'Pound Sterling', symbol: '£', rate: 0.785, eff: '07 Jun 2026' },
  { code: 'IRR', name: 'Iranian Rial', symbol: '﷼', rate: 598000, eff: '07 Jun 2026' },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺', rate: 38.4, eff: '06 Jun 2026' },
];
const FX_MAP: Record<string, FxRate> = Object.fromEntries(FX_RATES.map((c) => [c.code, c]));

/** Persian (۰-۹) and Arabic-Indic (٠-٩) digits → ASCII 0-9. */
function toEnDigits(s: string): string {
  return s
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06F0))
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660));
}

/** Toolbox Currency Converter — pick from/to, type an amount, swap directions. */
@Component({
  selector: 'app-currency-converter',
  standalone: true,
  imports: [IconComponent, DropdownComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './currency-converter.component.html',
})
export class CurrencyConverterComponent {
  readonly rates = FX_RATES;
  /** Design-system dropdown options; trigger shows the code, menu shows code — name. */
  readonly currencyOptions: DropdownOption[] = FX_RATES.map((c) => ({
    value: c.code, label: c.code + ' — ' + c.name, selectedLabel: c.code, meta: c.symbol,
  }));
  readonly amount = signal('100');
  readonly from = signal('USD');
  readonly to = signal('AED');

  readonly amt = computed(() => parseFloat(this.amount()));
  readonly valid = computed(() => isFinite(this.amt()));
  /** The editable amount with thousands separators (integer part grouped,
   *  decimals + a trailing '.' preserved). Raw digits live in {@link amount}. */
  readonly amountText = computed(() => {
    const s = this.amount();
    if (!s) return '';
    const [intPart, decPart] = s.split('.');
    const grouped = (intPart || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return s.includes('.') ? grouped + '.' + (decPart ?? '') : grouped;
  });
  /** 1 `from` expressed in `to`. */
  readonly pair = computed(() => FX_MAP[this.to()].rate / FX_MAP[this.from()].rate);
  readonly out = computed(() => (this.valid() ? this.amt() * this.pair() : 0));

  readonly fromSym = computed(() => FX_MAP[this.from()].symbol);
  readonly toSym = computed(() => FX_MAP[this.to()].symbol);
  readonly outText = computed(() => (this.valid() ? this.fmt(this.out(), this.to()) : '—'));
  readonly pairText = computed(() => this.fmt(this.pair(), this.to()));
  readonly eff = computed(() => FX_MAP[this.to()].eff);

  fmt(n: number, code: string): string {
    const dp = code === 'IRR' ? 0 : 2;
    return n.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
  }

  /** Accept digits only (plus a single decimal point). Persian/Arabic digits are
   *  converted to English; anything else is dropped and dots after the first are
   *  removed. The DOM input value is forced back to the grouped text so a rejected
   *  character can never linger — even when the sanitised model is unchanged (in
   *  which case the `[value]` binding wouldn't re-sync on its own). */
  onAmount(ev: Event): void {
    const el = ev.target as HTMLInputElement;
    let s = toEnDigits(el.value).replace(/[^0-9.]/g, '');
    const dot = s.indexOf('.');
    if (dot !== -1) s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, '');
    this.amount.set(s);
    el.value = this.amountText();
  }
  swap(): void { const f = this.from(); this.from.set(this.to()); this.to.set(f); }
}
